import { Link } from 'react-router-dom';
import { Wrench, ArrowLeft, Shield, Mail } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0F5156] text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy – Link-A-Pro</h1>
          </div>
          <div className="text-white/80 space-y-1">
            <p><strong>Effective Date:</strong> February 1, 2026</p>
            <p><strong>Last Updated:</strong> February 27, 2026</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Introduction */}
          <p className="text-gray-600 text-lg mb-12">
            Link-A-Pro ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you use our website and mobile application.
          </p>

          {/* 1. Information We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Information We Collect</h2>
            <p className="text-gray-600 mb-6">We may collect the following types of information:</p>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-[#0F5156]">🔹</span> Personal Information
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Full name</li>
                  <li>• Email address</li>
                  <li>• Phone number</li>
                  <li>• Profile photo</li>
                  <li>• Business information (if applicable)</li>
                  <li>• Service descriptions</li>
                  <li>• Payment details (processed securely through third-party providers)</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-[#0F5156]">🔹</span> Usage Data
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• IP address</li>
                  <li>• Device type</li>
                  <li>• Browser type</li>
                  <li>• App usage activity</li>
                  <li>• Log data</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-[#0F5156]">🔹</span> Location Data
                </h3>
                <p className="text-gray-600">
                  If enabled, we may collect approximate location data to help connect you with nearby professionals.
                </p>
              </div>
            </div>
          </section>

          {/* 2. How We Use Your Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We use your information to:</p>
            <ul className="space-y-2 text-gray-600 mb-6">
              <li>• Create and manage your account</li>
              <li>• Connect users with professionals</li>
              <li>• Process transactions</li>
              <li>• Improve platform functionality</li>
              <li>• Provide customer support</li>
              <li>• Send important notifications</li>
              <li>• Prevent fraud and maintain platform security</li>
            </ul>
            <div className="bg-[#0F5156]/10 rounded-xl p-4">
              <p className="text-[#0F5156] font-semibold">
                We do not sell your personal information to third parties.
              </p>
            </div>
          </section>

          {/* 3. Data Sharing */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Data Sharing</h2>
            <p className="text-gray-600 mb-4">We may share limited information:</p>
            <ul className="space-y-2 text-gray-600">
              <li>• With service providers (payment processors, hosting providers)</li>
              <li>• When required by law</li>
              <li>• To enforce our terms and policies</li>
              <li>• To protect users and prevent fraud</li>
            </ul>
          </section>

          {/* 4. Data Storage & Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Data Storage & Security</h2>
            <p className="text-gray-600 mb-4">
              We implement appropriate technical and organizational measures to protect your data.
            </p>
            <p className="text-gray-600">
              While we strive to use commercially acceptable means to protect your information, no method of transmission over the internet is 100% secure.
            </p>
          </section>

          {/* 5. Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Your Rights</h2>
            <p className="text-gray-600 mb-4">Depending on your location, you may have the right to:</p>
            <ul className="space-y-2 text-gray-600 mb-6">
              <li>• Access your data</li>
              <li>• Correct inaccurate information</li>
              <li>• Request deletion of your account</li>
              <li>• Withdraw consent</li>
              <li>• Request a copy of your stored data</li>
            </ul>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-gray-700">
                To exercise your rights, contact:<br />
                <a href="mailto:info@thetechxpert.com" className="text-[#0F5156] font-medium hover:underline flex items-center gap-2 mt-2">
                  <Mail className="w-4 h-4" /> info@thetechxpert.com
                </a>
              </p>
            </div>
          </section>

          {/* 6. Account Deletion */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Account Deletion</h2>
            <p className="text-gray-600 mb-4">Users may request account deletion by:</p>
            <ul className="space-y-2 text-gray-600 mb-4">
              <li>• Using the in-app deletion feature (if available), or</li>
              <li>• Emailing <a href="mailto:info@thetechxpert.com" className="text-[#0F5156] hover:underline">info@thetechxpert.com</a></li>
            </ul>
            <p className="text-gray-600">
              Upon verification, your account and associated personal data will be deleted in accordance with applicable laws.
            </p>
          </section>

          {/* 7. Third-Party Services */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Third-Party Services</h2>
            <p className="text-gray-600 mb-4">Link-A-Pro may use third-party services such as:</p>
            <ul className="space-y-2 text-gray-600 mb-4">
              <li>• Payment processors</li>
              <li>• Analytics providers</li>
              <li>• Cloud hosting services</li>
            </ul>
            <p className="text-gray-600">
              These third parties operate under their own privacy policies.
            </p>
          </section>

          {/* 8. Children's Privacy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Children's Privacy</h2>
            <p className="text-gray-600 mb-2">
              Link-A-Pro is not intended for users under the age of 18.
            </p>
            <p className="text-gray-600">
              We do not knowingly collect personal information from minors.
            </p>
          </section>

          {/* 9. Changes to This Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">9. Changes to This Policy</h2>
            <p className="text-gray-600">
              We may update this Privacy Policy periodically. Updates will be posted on this page with the revised date.
            </p>
          </section>

          {/* 10. Contact Us */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">10. Contact Us</h2>
            <p className="text-gray-600 mb-4">If you have any questions about this Privacy Policy:</p>
            <div className="bg-[#0F5156] text-white rounded-xl p-6">
              <a href="mailto:info@thetechxpert.com" className="flex items-center gap-2 text-lg font-medium hover:underline">
                <Mail className="w-5 h-5" /> info@thetechxpert.com
              </a>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="font-semibold">Link-A-Pro</p>
                <p className="text-white/80">A product of <span className="text-[#E07A5F]">The TechXpert</span></p>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
              <Wrench className="w-4 h-4 text-[#0F5156]" />
            </div>
            <span className="text-lg font-bold">Link-A-Pro</span>
          </div>
          <div className="flex justify-center gap-6 text-sm text-gray-400 mb-4">
            <Link to="/support" className="hover:text-white transition-colors">Support</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
          <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Link-A-Pro. All rights reserved.</p>
          <p className="text-gray-500 text-sm mt-2">Powered by <span className="text-[#E07A5F]">The TechXpert</span></p>
        </div>
      </footer>
    </div>
  );
}
